<template>
  <v-app-bar
    flat
    app
    height="64"
    class="app-header"
    elevation="4"
  >
    <v-container
      fluid
      class="pa-0"
    >
      <div
        style="
          display: flex;
          align-items: center;
          flex-direction: row;
          justify-content: space-between;
        "
      >
        <!-- Left section with logo + buttons -->
        <div class="header-left">
          <!-- VesselQUAD button -->
          <div
            class="logo-section"
            :class="{ active: activeView === 'vessel' }"
            @click="setActiveView('vessel')"
          >
            <img
              src="/VesselQUAD.ico"
              alt="Vessel QUAD Logo"
              class="logo-icon mr-3"
              width="36"
              height="36"
            >
            <h1 class="app-title">
              VesselQUAD
            </h1>
          </div>

          <!-- Fleet Dashboard button -->
          <v-btn
            v-show="viewSettings.useFleetDashboard"
            rounded
            class="fleet-btn"
            :color="activeView === 'fleet' ? 'primary' : 'grey darken-1'"
            dark
            @click="setActiveView('fleet')"
          >
            Fleet Dashboard
          </v-btn>

          <!-- Special Zones dropdown right next to Fleet Dashboard - only shown on home route -->
          <div
            v-if="$route.name === 'home' && activeView !== 'fleet'"
            class="header-special-zones"
          >
            <SpecialZonesDropdown
              :key="$route.fullPath"
              @zoneSelected="onSpecialZoneSelected"
            />
          </div>
        </div>

        <!-- Center (Search vessels) -->
        <div class="header-center">
          <VesselSelector />
        </div>

        <!-- Right actions -->
        <div class="header-action-btn-container">
          <NotificationsBadge />
          <AlertSettingsButton v-if="viewSettings.usesAlerts" />
          <UserManualButton />

          <SignOutDialog />
          <SwitchThemeButton v-if="viewSettings.canToggleTheme" />
        </div>
      </div>
    </v-container>
  </v-app-bar>
</template>

<script>
import NotificationsBadge from "@/views/notifications/Badge.vue";
import SignOutDialog from "@/views/home/components/SignOutDialog.vue";
import AlertSettingsButton from "@/views/home/components/AlertSettingsButton.vue";
import UserManualButton from "@/views/home/components/UserManualButton.vue";
import VesselSelector from "@/views/home/components/VesselSelector.vue";
import defaultViewSettings from "@/domain/view_settings/data";
import SwitchThemeButton from "@/views/home/components/SwitchThemeButton.vue";
import SpecialZonesDropdown from "@/views/weather_routing/map/components/SpecialZonesDropdown.vue";

import { mapState, mapActions } from "vuex";

export default {
  name: "Header",
  components: {
    NotificationsBadge,
    SignOutDialog,
    AlertSettingsButton,
    VesselSelector,
    SwitchThemeButton,
    SpecialZonesDropdown,
    UserManualButton,
  },
  data() {
    return {
      viewSettings: defaultViewSettings,
      activeView: this.$route.query.view || "vessel",
    };
  },
  computed: {
    ...mapState("main", ["theme"]),
    themeIcon() {
      return this.theme === "dark" ? "mdi-weather-sunny" : "mdi-weather-night";
    },
  },
  watch: {
    "$route.query.view"(newView) {
      this.activeView = newView || "vessel";
    },
  },
  methods: {
    ...mapActions("weatherRouting/specialZones", ["loadZone"]),

    onSpecialZoneSelected(zone) {
      this.loadZone({ zoneType: zone });
    },

    setActiveView(view) {
      this.activeView = view;

      this.$router.push({ name: "home", query: { view } });

      if (view === "vessel") {
        this.$store.commit("main/SET_SELECTED_VESSEL", null);
      }
    },
  },
};
</script>

<style scoped>
.app-header {
  background: linear-gradient(
    135deg,
    var(--color-primary) 0%,
    var(--color-primary) 50%,
    var(--color-primary) 80%
  ) !important;
  border: none !important;
}

.app-header::v-deep .v-toolbar__content {
  padding: 0 !important;
}

/* Left section */
.header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.logo-section {
  padding-left: 24px;
  cursor: pointer;
  display: flex;
  align-items: center;
}

.app-title {
  font-size: 20px;
  font-weight: 600;
  margin: 0;
  letter-spacing: -0.5px;
  color: var(--color-text);
}

.logo-icon {
  filter: brightness(0) invert(1);
  transition: all 0.3s ease;
}

.logo-icon:hover {
  transform: scale(1.05);
}

/* Center stays fixed (won't move) */
.header-center {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
}

.header-action-btn-container {
  display: flex;
  align-items: center;
  margin-right: 16px;
  gap: 8px;
}

.fleet-btn {
  border: 1px solid #2c3e50;
  border-radius: 4px;
  background-color: #34495e !important;
  color: #ecf0f1 !important;
  font-weight: 600;
  font-size: 14px;
  width: 180px;
  height: 36px;
  text-transform: none;
}

.header-special-zones {
  display: flex;
  align-items: center;
  margin-left: 6px;
}
</style>
